import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Camera } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const abs = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);

/** Every photo URL a log carries (new list first, legacy single fallback). */
export function logPhotoUrls(log) {
  if (!log) return [];
  if (Array.isArray(log.photo_urls) && log.photo_urls.length) return log.photo_urls;
  return log.photo_url ? [log.photo_url] : [];
}

/** Full-screen viewer with swipe-through prev/next. */
export function PhotoLightbox({ urls, index, onClose, onIndex }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex((index + 1) % urls.length);
      if (e.key === "ArrowLeft") onIndex((index - 1 + urls.length) % urls.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, urls.length, onClose, onIndex]);

  if (index == null || !urls.length) return null;
  const stop = (e) => e.stopPropagation();
  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center"
      onClick={(e) => { stop(e); onClose(); }}
      data-testid="photo-lightbox"
      role="dialog"
      aria-modal="true"
    >
      <img
        src={abs(urls[index])}
        alt={`Photo ${index + 1} of ${urls.length}`}
        className="max-h-[92vh] max-w-[96vw] object-contain select-none"
        onClick={stop}
        data-testid="photo-lightbox-img"
      />
      <button
        type="button"
        onClick={(e) => { stop(e); onClose(); }}
        className="absolute top-3 right-3 p-2 text-white/80 hover:text-white bg-black/40 rounded-full"
        aria-label="Close"
        data-testid="photo-lightbox-close"
      >
        <X size={22} />
      </button>
      {urls.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => { stop(e); onIndex((index - 1 + urls.length) % urls.length); }}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white bg-black/40 rounded-full"
            aria-label="Previous photo"
            data-testid="photo-lightbox-prev"
          >
            <ChevronLeft size={26} />
          </button>
          <button
            type="button"
            onClick={(e) => { stop(e); onIndex((index + 1) % urls.length); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white bg-black/40 rounded-full"
            aria-label="Next photo"
            data-testid="photo-lightbox-next"
          >
            <ChevronRight size={26} />
          </button>
          <div className="absolute bottom-4 inset-x-0 text-center text-white/80 text-[11px] tracking-[0.2em] font-mono-tir" data-testid="photo-lightbox-counter">
            {index + 1} / {urls.length}
          </div>
        </>
      )}
    </div>,
    document.body
  );
}

/**
 * Thumbnail strip for a workout log's photos. Tap → full-screen lightbox.
 * size: "xs" (planner cards) | "sm" (feeds) | "md" (modals).
 */
export function LogPhotoStrip({ log, urls: urlsProp, size = "sm", testid = "log-photos", max = 4 }) {
  const urls = urlsProp || logPhotoUrls(log);
  const [open, setOpen] = useState(null);
  if (!urls.length) return null;
  const dim = size === "xs" ? "h-10 w-10" : size === "md" ? "h-24 w-24" : "h-16 w-16";
  const shown = urls.slice(0, max);
  const extra = urls.length - shown.length;
  return (
    <>
      <div className="flex items-center gap-1 flex-wrap" data-testid={testid} onClick={(e) => e.stopPropagation()}>
        <span className="inline-flex items-center gap-0.5 text-[9px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-burgundy)] mr-0.5" data-testid={`${testid}-count`}>
          <Camera size={10} /> {urls.length}
        </span>
        {shown.map((u, i) => (
          <button
            key={`${u}-${i}`}
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(i); }}
            className={`${dim} relative shrink-0 border border-[var(--tir-border)] hover:border-[var(--tir-burgundy)] overflow-hidden bg-white`}
            aria-label={`Open photo ${i + 1} of ${urls.length}`}
            data-testid={`${testid}-thumb-${i}`}
          >
            <img src={abs(u)} alt={`Photo ${i + 1}`} loading="lazy" className="h-full w-full object-cover" />
            {i === shown.length - 1 && extra > 0 && (
              <span className="absolute inset-0 bg-black/55 text-white text-xs font-semibold flex items-center justify-center">+{extra}</span>
            )}
          </button>
        ))}
      </div>
      <PhotoLightbox urls={urls} index={open} onClose={() => setOpen(null)} onIndex={setOpen} />
    </>
  );
}
